import { SectionTitle, SectionTitleSizes } from '@components/common/section-title';
import { useI18NextContext } from '@components/hooks';
import { Button, ButtonColor, ButtonSize, ButtonVariant } from '@components/material-ui';
import { TestId } from '@components/test-ids';
import styled from '@emotion/styled';
import Grid from '@mui/material/Grid';
import { Resort } from '@model/iceberg/geography';
import { Theme, ThemeProps } from '@theme/base';
import { withTheme } from '@emotion/react';
import React from 'react';
import { Tile } from './tile/Tile';
import { ResortsLink } from '@components/common/next-link/resorts-link';

/* ***************** *
 *       Types       *
 * ***************** */

export interface ExploreResortsProps {
  theme?: Theme;
  testId?: string;
  title: string;
  ctaTitle?: string;
  path: string;
  resorts: Array<Resort>;
  className?: string;
}

// Not all destinations have 3 resorts.
// This will ensure that we take up the correct space dependent on number of resorts.
export const getTileSize = (resorts: number, index: number) => {
  switch (resorts) {
    case 1:
      return 12;
    case 2:
      return 6;
    case 3:
      return index === 0 ? 12 : 6;
    default:
      return 6;
  }
};

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div({});

const ViewMoreResortsButton = styled(Button)(({ theme }: ThemeProps) => ({
  marginTop: theme.custom.spacing.xxLarge
}));

const TOP_RESORTS: number = 4;

/* ********************************* *
 *    ExploreResorts                 *
 * ********************************* */

export const ExploreResortsComponent = ({
  theme,
  testId,
  resorts,
  title,
  ctaTitle,
  path,
  className
}: ExploreResortsProps) => {
  const t: any = useI18NextContext();
  const topResorts = resorts.sort((a, b) => b.hotels - a.hotels).slice(0, TOP_RESORTS);
  const totalTopResorts: number = topResorts.length;
  const totalResorts: number = resorts.length;
  if (!totalResorts) return null;
  return (
    <Container className={className} data-testid={testId || TestId.exploreResort.exploreResortGroup.main}>
      <SectionTitle heading={t('destination__explore--title', { destination: title })} size={SectionTitleSizes.LARGE} />
      <Grid container spacing={theme!.custom.gridSpacing.default}>
        {topResorts.map((resort: Resort, index: number) => (
          <Grid item xs={12} sm={getTileSize(totalTopResorts, index)} key={resort.name.path}>
            <Tile name={resort.name.display} count={resort.hotels} path={resort.name.path} />
          </Grid>
        ))}
      </Grid>
      {totalResorts > 4 && (
        <ResortsLink path={path}>
          <ViewMoreResortsButton
            variant={ButtonVariant.OUTLINED}
            size={ButtonSize.REGULAR}
            color={ButtonColor.SECONDARY}
            label={{
              label: ctaTitle || 'explore-resorts__label--view-all-resorts',
              vars: { destinationName: title }
            }}
          />
        </ResortsLink>
      )}
    </Container>
  );
};

export const ExploreResorts = withTheme(ExploreResortsComponent);
